import { useCallback, useEffect } from "react";
import { Database } from "@Shape-Digital/kudzu-data/lib/types/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import useDataState from "./useDataState";
import { useSupabaseClient } from "../common/supabaseClient";

type SupabaseSystemConfig = DatabaseRow<"system_configs">;

export type SystemConfig = {
  id: string;
  discountAmount: number;
  defaultLanguageCode: string;
  defaultCurrencyCode: string;
  defaultOfferingDurationMinutes: number;
  appointmentCheckoutDurationSeconds: number;
  appointmentCheckoutMaxRepeatCount: number;
  minimumTimeBeforeBookingSeconds: number;
  upsellDurationMinutes: number;
  minOnlineBookingAge: number;
};

const DEFAULT_UPSELL_DURATION_MIN = 5;

const parseSystemConfig = (supabaseSystemConfig: SupabaseSystemConfig) => ({
  id: supabaseSystemConfig.id,
  discountAmount: supabaseSystemConfig.discount_amount,
  defaultLanguageCode: supabaseSystemConfig.default_language_code,
  defaultCurrencyCode: supabaseSystemConfig.default_currency_code,
  defaultOfferingDurationMinutes:
    supabaseSystemConfig.default_offering_duration_minutes,
  appointmentCheckoutDurationSeconds:
    supabaseSystemConfig.appointment_checkout_duration_seconds,
  appointmentCheckoutMaxRepeatCount:
    supabaseSystemConfig.appointment_checkout_max_repeat_count,
  minimumTimeBeforeBookingSeconds:
    supabaseSystemConfig.minimum_time_before_booking_seconds,
  upsellDurationMinutes:
    supabaseSystemConfig.upsell_duration_display_min ||
    DEFAULT_UPSELL_DURATION_MIN,
  minOnlineBookingAge: supabaseSystemConfig.min_online_booking_age || 0,
});

export const getSystemConfig = async (supabase: SupabaseClient<Database>) => {
  const response = await supabase
    .from("system_configs")
    .select()
    .single<DatabaseRow<"system_configs">>();

  if (response.error) {
    throw new Error(response.error.message);
  }

  const parsedSystemConfig = parseSystemConfig(response.data);
  return parsedSystemConfig;
};

const useSystemConfig = () => {
  const { dataState, updateDataState } = useDataState<SystemConfig>();
  const supabase = useSupabaseClient();

  const sendRequest = useCallback(async () => {
    if (!supabase) return;
    try {
      updateDataState({ status: "loading" });

      const data = await getSystemConfig(supabase);
      updateDataState({ status: "success", data });
    } catch (err) {
      updateDataState({ status: "error", error: (err as Error).message });
    }
  }, [updateDataState, supabase]);

  useEffect(() => {
    sendRequest();
  }, [sendRequest]);

  return dataState;
};

export default useSystemConfig;
